import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { Form, PrimaryButton, Button, FieldTextInput, NamedLink, InlineTextButton, FieldCheckbox, SecondaryButton } from '../../../components';

import css from './LoginForm.module.css';
import eyeIcon from '../../../assets/icons/eye.svg';
import eyeSlashIcon from '../../../assets/icons/Union.svg';
import { ReactComponent as ExclamationIcon } from '../../../assets/icons/exclaimation-circle/solid.svg';


const LoginFormComponent = props => {
  const [showPassword, setShowPassword] = React.useState(false);
  const [hideUnverifiedTooltip, setHideUnverifiedTooltip] = React.useState(false);

  return (

  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        intl,
        invalid,
        values,
        errors,
        onOpenPasswordRecovery,
        onClose,
        loginError,
        onResendVerificationEmail,
        sendVerificationEmailInProgress,
      } = fieldRenderProps;

      // email
      const emailPlaceholder = intl.formatMessage({
        id: 'LoginForm.emailPlaceholder',
      });
      const emailRequiredMessage = intl.formatMessage({
        id: 'LoginForm.emailRequired',
      });
      const emailRequired = validators.required(emailRequiredMessage);
      const emailInvalidMessage = intl.formatMessage({
        id: 'LoginForm.emailInvalid',
      });
      const emailValid = validators.emailFormatValid(emailInvalidMessage);

      // password
      const passwordPlaceholder = intl.formatMessage({
        id: 'LoginForm.passwordPlaceholder',
      });
      const passwordRequiredMessage = intl.formatMessage({
        id: 'LoginForm.passwordRequired',
      });
      const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = invalid || submitInProgress;

      const passwordRecoveryLink = onOpenPasswordRecovery ? (
        <InlineTextButton
          type="button"
          rootClassName={css.recoveryLink}
          onClick={() => {
            const emailValid = values?.email && !errors?.email ? values.email : null;
            onOpenPasswordRecovery(emailValid);
          }}
        >
          <FormattedMessage id="LoginForm.forgotPassword" />
        </InlineTextButton>
      ) : (
        <NamedLink
          name="PasswordRecoveryPage"
          className={css.recoveryLink}
          to={{
            search:
              values?.email && !errors?.email ? `email=${encodeURIComponent(values.email)}` : '',
          }}
        >
          <FormattedMessage id="LoginForm.forgotPassword" />
        </NamedLink>
      );

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <div>
            <FieldTextInput
              type="email"
              id={formId ? `${formId}.email` : 'email'}
              name="email"
              autoComplete="email"
              placeholder={`${emailPlaceholder} *`}
              validate={validators.composeValidators(emailRequired, emailValid)}
            />
            <div className={classNames(css.password, css.passwordField)}>
              <FieldTextInput
                className={css.password}
                inputRootClass={css.passwordInput}
                type={showPassword ? 'text' : 'password'}
                id={formId ? `${formId}.password` : 'password'}
                name="password"
                autoComplete="current-password"
                placeholder={`${passwordPlaceholder} *`}
                validate={passwordRequired}
                customErrorText={loginError && loginError.message !== 'Email not verified' ? intl.formatMessage({ id: 'AuthenticationPage.loginFailed' }) : null}
              />
              <button
                type="button"
                className={css.passwordToggle}
                onClick={() => setShowPassword(s => !s)}
                aria-label={showPassword ? intl.formatMessage({ id: 'SignupForm.hidePassword', defaultMessage: 'Hide password' }) : intl.formatMessage({ id: 'SignupForm.showPassword', defaultMessage: 'Show password' })}
                aria-pressed={showPassword}
              >
                <img src={showPassword ? eyeIcon : eyeSlashIcon} alt="" />
              </button>
            </div>
            {loginError && loginError.message === 'Email not verified' && !hideUnverifiedTooltip ? (
              <div className={css.unverifiedTooltip}>
                <ExclamationIcon className={css.exclamationIcon} />
                <div>
                  <div className={css.unverifiedText}>
                    <FormattedMessage id="AuthenticationPage.emailNotVerified" />
                  </div>
                  <div className={css.resendWrapper}>
                    <InlineTextButton
                      type="button"
                      rootClassName={css.resendVerificationButton}
                      onClick={() => {
                        setHideUnverifiedTooltip(true);
                        if (onResendVerificationEmail) onResendVerificationEmail(values?.email, values?.password);
                      }}
                      disabled={sendVerificationEmailInProgress}
                    >
                      <FormattedMessage id="AuthenticationPage.resendEmailLinkText" />
                    </InlineTextButton>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
          <div className={css.bottomWrapper}>
            <div className={css.bottomWrapperInfo}>
              <FieldCheckbox
                id={formId ? `${formId}.rememberMe` : 'rememberMe'}
                name="rememberMe"
                label="Angemeldet bleiben"
                className={css.rememberUserCheckbox}
              />
              <p className={css.bottomWrapperText}>
                <span className={css.recoveryLinkInfo}>
                  <FormattedMessage
                    id="LoginForm.forgotPasswordInfo"
                    values={{ passwordRecoveryLink }}
                  />
                </span>
              </p>
            </div>
            <div className={css.modalButtons}>
              {onClose ? (
                <SecondaryButton type="button" onClick={onClose} className={css.cancelButton}>
                  Abbrechen
                </SecondaryButton>
              ) : null}
              <PrimaryButton
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
                className={css.submitButton}
              >
                <FormattedMessage id="LoginForm.logIn" />
              </PrimaryButton>
            </div>
          </div>
        </Form>
      );
    }}
  />
);
};

/**
 * A component that renders the login form.
 *
 * @component
 * @param {Object} props
 * @param {string} props.rootClassName - The root class name that overrides the default class css.root
 * @param {string} props.className - The class that extends the root class
 * @param {string} props.formId - The form id
 * @param {boolean} props.inProgress - Whether the form is in progress
 * @returns {JSX.Element}
 */
const LoginForm = props => {
  const intl = useIntl();
  return <LoginFormComponent {...props} intl={intl} />;
};

export default LoginForm;
